import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDarkMode } from '@/hooks/useDarkMode';

import { logger } from '@/utils/logger';
import { Gift, Heart, Plus } from 'lucide-react';
import AddRecipientDialog from '@/components/keepsakes/AddRecipientDialog';
import { Button } from '@/components/ui/button';
import WishlistShareModal from '@/components/keepsakes/WishlistShareModal';
import MemoryBox from '@/components/keepsakes/MemoryBox';
import CreateWishlistModal from '@/components/keepsakes/CreateWishlistModal';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import EditWishlistModal from '@/components/keepsakes/EditWishlistModal';
import ConfirmModal from '@/components/ConfirmModal';
import EditRecipientModal from '@/components/keepsakes/EditRecipientModal';
import WelcomeBanner from '@/components/WelcomeBanner';
import OccasionsModal from '@/components/OccasionsModal';
import KeepsakesHeader from '@/components/keepsakes/KeepsakesHeader';
import WishlistCard from '@/components/keepsakes/WishlistCard';
import Footer from '@/components/Footer';
// Import all keepsake components
import {
  relationshipConfig,
  colorOptions
} from '@/components/keepsakes';

export default function Keepsakes({ user }) {
  const navigate = useNavigate();
  const darkMode = useDarkMode();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'keepsakes';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [recipients, setRecipients] = useState([]);
  const [savedGiftsByRecipient, setSavedGiftsByRecipient] = useState({});
  const [questionnaires, setQuestionnaires] = useState([]);
  const [openBoxId, setOpenBoxId] = useState(null);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Wishlist state
  const [wishlists, setWishlists] = useState([]);
  const [wishlistProducts, setWishlistProducts] = useState({});
  // openWishlistId drives the Wishlist Dialog modal, matching the Keepsakes
  // tab "click card → open modal" pattern (replaces the old inline expand).
  const [openWishlistId, setOpenWishlistId] = useState(null);
  const [showCreateWishlist, setShowCreateWishlist] = useState(false);
  const [showEditWishlist, setShowEditWishlist] = useState(false);
  const [editingWishlist, setEditingWishlist] = useState(null);
  const [sharingWishlist, setSharingWishlist] = useState(null);
  
  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: 'Delete'
  });

  const [occasionsModal, setOccasionsModal] = useState({
    isOpen: false,
    recipient: null
  });

  const [editRecipientModal, setEditRecipientModal] = useState({
    isOpen: false,
    recipient: null,
    formData: { name: '', relationship: '', interests: [], notes: '', color: '#9E6752' }
  });

  const [newRecipient, setNewRecipient] = useState({
    name: '',
    relationship: '',
    gender: '',
    interests: [],
    personality: '',
    notes: '',
    occasions: []
  });

  const [analyzingId, setAnalyzingId] = useState(null);

  // Auto-open the recipient modal when arriving with ?recipientName=…
  // (used by the back-button on questionnaire pages so context survives
  // the round-trip without dumping the user back at the Keepsakes index).
  useEffect(() => {
    const wantedName = searchParams.get('recipientName');
    if (!wantedName || !recipients.length) return;
    const match = recipients.find(
      (r) => r.name.toLowerCase() === wantedName.toLowerCase()
    );
    if (match) setOpenBoxId(match.id);
  }, [searchParams, recipients]);

  // Inline analyze, used by the recipient card so the user doesn't need
  // a detour through the manage page just to hit Analyze.
  const handleAnalyzeInline = async (questionnaireId) => {
    setAnalyzingId(questionnaireId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/keepsakes/${questionnaireId}/analyze`,
        { method: 'POST', credentials: 'include' }
      );
      let bodyText = '';
      try { bodyText = await res.text(); } catch (_) { /* ignored */ }
      if (!res.ok) {
        let detail = `Analysis failed (status ${res.status})`;
        try {
          const parsed = JSON.parse(bodyText);
          if (parsed?.detail) detail = parsed.detail;
        } catch (_) { /* non-JSON body */ }
        throw new Error(detail);
      }
      toast.success('Analysis complete!');
      navigate(`/keepsakes/${questionnaireId}/results`);
    } catch (err) {
      logger.error('Analyze failed:', err);
      toast.error(err.message || 'Failed to analyze');
    } finally {
      setAnalyzingId(null);
    }
  };

  const fetchRecipients = useCallback(async () => {
    try {
      const response = await api.get('/recipients');
      const list = response.data || [];
      setRecipients(list);
      
      const giftsMap = {};
      for (const r of list) {
        try {
          const giftsRes = await api.get(`/recipients/${r.id}/saved-gifts`);
          giftsMap[r.id] = giftsRes.data;
        } catch {
          giftsMap[r.id] = [];
        }
      }
      setSavedGiftsByRecipient(giftsMap);

      // Note: we intentionally do NOT auto-select a recipient on load.
      // openBoxId drives the detail Dialog modal, auto-selecting would
      // pop the first recipient's profile open every time the user lands
      // on the Keepsakes page.
    } catch (error) {
      toast.error('Failed to load recipients');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuestionnaires = useCallback(async () => {
    try {
      const response = await api.get('/keepsakes');
      setQuestionnaires(response.data);
    } catch (error) {
      logger.error('Failed to load questionnaires');
    }
  }, []);

  const fetchWishlistProducts = useCallback(async (wishlistId) => {
    try {
      const response = await api.get(`/wishlists/${wishlistId}/products`);
      setWishlistProducts(prev => ({
        ...prev,
        [wishlistId]: response.data
      }));
    } catch (error) {
      logger.error(`Failed to load products for wishlist ${wishlistId}:`, error);
    }
  }, []);

  const fetchWishlists = useCallback(async () => {
    try {
      const response = await api.get('/wishlists');
      setWishlists(response.data);
      for (const wishlist of response.data) {
        fetchWishlistProducts(wishlist.id);
      }
    } catch (error) {
      logger.error('Failed to load wishlists:', error);
    }
  }, [fetchWishlistProducts]);

  // Data fetching (registered below the fetch callbacks to satisfy TDZ)
  useEffect(() => {
    fetchRecipients();
    fetchQuestionnaires();
    fetchWishlists();

    const handleKeepsakeUpdate = (event) => {
      if (event.detail.recipientId) {
        api.get(`/recipients/${event.detail.recipientId}/saved-gifts`)
          .then(res => {
            setSavedGiftsByRecipient(prev => ({
              ...prev,
              [event.detail.recipientId]: res.data
            }));
          })
          .catch(err => logger.error('Failed to refresh saved gifts:', err));
      }
    };

    window.addEventListener('keepsakeUpdated', handleKeepsakeUpdate);
    return () => window.removeEventListener('keepsakeUpdated', handleKeepsakeUpdate);
  }, [fetchRecipients, fetchQuestionnaires, fetchWishlists]);

  // Recipient handlers
  const handleAddRecipient = async (e) => {
    e.preventDefault();
    try {
      // Randomly select a color from the color options
      const randomColor = colorOptions[Math.floor(Math.random() * colorOptions.length)];
      
      const formattedRecipient = {
        ...newRecipient,
        color: randomColor,
        birthday: newRecipient.birthday ? newRecipient.birthday.slice(5) : null,
        anniversary: newRecipient.anniversary ? newRecipient.anniversary.slice(5) : null
      };
      
      const response = await api.post('/recipients', formattedRecipient);
      setRecipients([...recipients, response.data]);
      setSavedGiftsByRecipient({ ...savedGiftsByRecipient, [response.data.id]: [] });
      toast.success('Keepsake created!');
      setShowAddRecipient(false);
      setNewRecipient({ name: '', relationship: '', gender: '', interests: [], personality: '', notes: '', occasions: [] });
      setOpenBoxId(response.data.id);
    } catch (error) {
      toast.error('Failed to add recipient');
    }
  };

  const handleDeleteRecipient = async (recipientId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Keepsake?',
      message: 'This will permanently delete this keepsake and all saved gifts. This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(`/recipients/${recipientId}`);
          setRecipients(recipients.filter(r => r.id !== recipientId));
          const newGiftsMap = { ...savedGiftsByRecipient };
          delete newGiftsMap[recipientId];
          setSavedGiftsByRecipient(newGiftsMap);
          if (openBoxId === recipientId) setOpenBoxId(null);
          toast.success('Keepsake deleted');
        } catch (error) {
          toast.error('Failed to delete');
        }
      }
    });
  };

  const handleEditRecipient = (recipient) => {
    const config = relationshipConfig[recipient.relationship] || relationshipConfig['Friend'];
    const prefs = recipient.gift_preferences || {};
    // Merge the (legacy) separate preference interests/notes into the single
    // profile fields so there's one source of truth going forward.
    const mergedInterests = Array.from(new Set([...(recipient.interests || []), ...(prefs.interests || [])]));
    setEditRecipientModal({
      isOpen: true,
      recipient,
      formData: {
        name: recipient.name,
        relationship: recipient.relationship,
        gender: recipient.gender || '',
        interests: mergedInterests,
        occasions: recipient.occasions || [],
        notes: recipient.notes || prefs.notes || '',
        color: recipient.color || config.color,
        personality: prefs.personality || '',
        budget_range: prefs.budget_range || ''
      }
    });
  };

  const handleSaveRecipient = async () => {
    try {
      const { personality, budget_range, ...base } = editRecipientModal.formData;
      await api.put(`/recipients/${editRecipientModal.recipient.id}`, {
        ...base,
        gift_preferences: {
          personality: personality || '',
          budget_range: budget_range || '',
          interests: base.interests || [],
          notes: base.notes || ''
        }
      });
      toast.success('Recipient updated!');
      setEditRecipientModal({ isOpen: false, recipient: null, formData: {} });
      fetchRecipients();
    } catch (error) {
      logger.error('Failed to update recipient:', error);
      toast.error('Failed to update recipient');
    }
  };

  const handleRemoveSavedGift = async (recipientId, savedGiftId) => {
    try {
      await api.delete(`/saved-gifts/${savedGiftId}`);
      setSavedGiftsByRecipient({
        ...savedGiftsByRecipient,
        [recipientId]: savedGiftsByRecipient[recipientId].filter(g => g.saved_gift.id !== savedGiftId)
      });
      toast.success('Gift removed');
    } catch (error) {
      toast.error('Failed to remove gift');
    }
  };

  // Questionnaire handlers
  const handleCreateQuestionnaire = (recipient) => {
    window.location.href = `/keepsakes/create?name=${encodeURIComponent(recipient.name)}`;
  };

  const handleDeleteQuestionnaire = async (questionnaireId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Questionnaire?',
      message: 'This will permanently delete this questionnaire and all its responses. This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(`/keepsakes/${questionnaireId}`);
          await fetchQuestionnaires();
          toast.success('Questionnaire deleted');
        } catch (error) {
          toast.error('Failed to delete questionnaire');
        }
      }
    });
  };

  // Preferences are edited inside the Edit Recipient modal (consolidated).

  const handleFindGifts = (recipient) => {
    navigate('/browse', { 
      state: { 
        fromPreferences: true,
        recipientName: recipient.name,
        recipientProfile: {
          interests: recipient.interests || [],
          relationship: recipient.relationship,
          gender: recipient.gender || '',
          notes: recipient.notes || '',
          occasions: recipient.occasions || [],
          preferences: recipient.gift_preferences || {}
        }
      } 
    });
  };

  // Occasions handlers
  const handleManageOccasions = (recipient) => {
    setOccasionsModal({ isOpen: true, recipient });
  };

  const handleUpdateRecipientOccasions = async (updatedRecipient) => {
    try {
      await api.put(`/recipients/${updatedRecipient.id}`, {
        occasions: updatedRecipient.occasions,
        reminder_timing: updatedRecipient.reminder_timing
      });
      setRecipients(recipients.map(r => 
        r.id === updatedRecipient.id ? updatedRecipient : r
      ));
    } catch (error) {
      logger.error('Error updating recipient occasions:', error);
      throw error;
    }
  };

  // New recipient form handlers
  const handleInterestToggle = (interest) => {
    setNewRecipient(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleAddOccasion = (occasion) => {
    const alreadyExists = newRecipient.occasions.find(o => o.name === occasion);
    if (!alreadyExists) {
      setNewRecipient(prev => ({
        ...prev,
        occasions: [...prev.occasions, { name: occasion, date: '' }]
      }));
    }
  };

  const handleRemoveOccasion = (occasionName) => {
    setNewRecipient(prev => ({
      ...prev,
      occasions: prev.occasions.filter(o => o.name !== occasionName)
    }));
  };

  const handleOccasionDateChange = (occasionName, date) => {
    setNewRecipient(prev => ({
      ...prev,
      occasions: prev.occasions.map(o => 
        o.name === occasionName ? { ...o, date } : o
      )
    }));
  };

  // Wishlist handlers
  // (Wishlist open/close is now driven by openWishlistId + a Dialog modal,
  //  mirroring the Keepsakes tab pattern.)

  const handleCreateWishlist = async (data) => {
    try {
      await api.post('/wishlists', data);
      toast.success('Wishlist created!');
      setShowCreateWishlist(false);
      fetchWishlists();
    } catch (error) {
      toast.error('Failed to create wishlist');
    }
  };

  const handleUpdateWishlist = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/wishlists/${editingWishlist.id}`, {
        name: formData.get('name'),
        color: formData.get('color')
      });
      toast.success('Wishlist updated!');
      setShowEditWishlist(false);
      setEditingWishlist(null);
      fetchWishlists();
    } catch (error) {
      logger.error('Failed to update wishlist:', error);
      toast.error('Failed to update wishlist');
    }
  };

  const handleDeleteWishlist = async (wishlistId) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Wishlist?',
      message: 'This will permanently delete this wishlist and all saved products. This action cannot be undone.',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await api.delete(`/wishlists/${wishlistId}`);
          toast.success('Wishlist deleted');
          fetchWishlists();
        } catch (error) {
          logger.error('Failed to delete wishlist:', error);
          toast.error('Failed to delete wishlist');
        }
      }
    });
  };

  const handleRemoveProductFromWishlist = async (wishlistId, productId) => {
    try {
      await api.delete(`/wishlists/${wishlistId}/products/${productId}`);
      toast.success('Product removed from wishlist');
      fetchWishlistProducts(wishlistId);
    } catch (error) {
      logger.error('Failed to remove product:', error);
      toast.error('Failed to remove product');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center transition-colors" style={{ background: darkMode.bgPrimary }}>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 relative">
            <div className="absolute inset-0 rounded-2xl animate-pulse" style={{ background: '#FED7A5' }} />
            <Gift className="w-8 h-8 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ color: '#9E6752' }} />
          </div>
          <div className="text-xl transition-colors" style={{ color: darkMode.textSecondary, fontFamily: 'Playfair Display, serif' }}>
            Opening your keepsakes...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors page-enter" style={{ background: darkMode.bgPrimary }}>
      {/* Header */}
      <KeepsakesHeader darkMode={darkMode} />

      {/* Tab Navigation */}
      <div className="border-b transition-colors" style={{ borderColor: darkMode.borderPrimary, background: darkMode.bgSecondary }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('keepsakes')}
              className="py-4 px-2 font-medium transition-all relative"
              style={{ color: activeTab === 'keepsakes' ? '#FED7A5' : darkMode.textSecondary }}
            >
              <Gift className="w-4 h-4 inline mr-2 mb-1" />
              Keepsakes
              {activeTab === 'keepsakes' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: '#FED7A5' }} />
              )}
            </button>
            <button
              onClick={() => setActiveTab('wishlist')}
              className="py-4 px-2 font-medium transition-all relative"
              style={{ color: activeTab === 'wishlist' ? '#FED7A5' : darkMode.textSecondary }}
            >
              <Heart className="w-4 h-4 inline mr-2 mb-1" />
              My Wishlist
              {activeTab === 'wishlist' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: '#FED7A5' }} />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        {/* First-run onboarding, only renders for users with zero
            recipients AND zero wishlists, and only when not previously
            dismissed. Self-hides otherwise. */}
        <WelcomeBanner
          user={user}
          hasActivity={(recipients?.length || 0) + (wishlists?.length || 0) > 0}
          darkMode={darkMode}
        />

        {activeTab === 'keepsakes' ? (
          /* Keepsakes Tab Content */
          !recipients || recipients.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <div className="relative w-48 h-48 mx-auto mb-8">
                  <div className="absolute inset-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)', transform: 'rotate(-5deg)' }} />
                  <div className="absolute inset-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #B4D4E8 0%, #A4C4D8 100%)', transform: 'rotate(5deg)' }} />
                  <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)' }}>
                    <Gift className="w-16 h-16" style={{ color: 'white' }} />
                  </div>
                </div>
                
                <h2 className="text-3xl mb-4 transition-colors" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: '600' }}>
                  Start Your Collection
                </h2>
                <p className="text-base mb-8 leading-relaxed transition-colors" style={{ color: darkMode.textSecondary }}>
                  Create keepsakes for the people you love. Save gift ideas, track their interests, and never be stuck wondering what to give.
                </p>
                <Button
                  onClick={() => setShowAddRecipient(true)}
                  data-testid="add-first-recipient-button"
                  className="transition-all hover:scale-105"
                  style={{ background: '#FED7A5', color: '#20212B', borderRadius: '30px', padding: '16px 32px', fontSize: '16px', fontWeight: '600' }}
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Create Your First Keepsake
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Decorative grid of keepsake boxes, click a card to open
                  its full detail view in a modal overlay. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {recipients.map((recipient) => (
                  <MemoryBox
                    key={recipient.id}
                    recipient={recipient}
                    savedGifts={savedGiftsByRecipient[recipient.id] || []}
                    questionnaires={questionnaires}
                    isOpen={false}
                    onToggle={() => setOpenBoxId(recipient.id)}
                    onDelete={() => handleDeleteRecipient(recipient.id)}
                    onAddGift={() => {
                      navigate('/browse', {
                        state: {
                          addGiftMode: true,
                          recipientId: recipient.id,
                          recipientName: recipient.name,
                          interests: recipient.interests || [],
                          preferences: recipient.gift_preferences || {}
                        }
                      });
                    }}
                    onRemoveGift={(giftId) => handleRemoveSavedGift(recipient.id, giftId)}
                    onCreateQuestionnaire={() => handleCreateQuestionnaire(recipient)}
                    onDeleteQuestionnaire={handleDeleteQuestionnaire}
                    onAnalyzeQuestionnaire={handleAnalyzeInline}
                    analyzingId={analyzingId}
                    onManageOccasions={() => handleManageOccasions(recipient)}
                    onEditRecipient={() => handleEditRecipient(recipient)}
                    onFindGifts={handleFindGifts}
                    onInlineGiftAdded={() => fetchRecipients()}
                    user={user}
                    darkMode={darkMode}
                  />
                ))}
              </div>

              {/* Expanded detail modal, opens for the currently-selected
                  recipient when their card is clicked. */}
              <Dialog
                open={!!openBoxId}
                onOpenChange={(open) => { if (!open) setOpenBoxId(null); }}
              >
                <DialogContent
                  className="max-w-7xl w-[95vw] max-h-[90vh] p-0 border-0 bg-transparent shadow-none rounded-2xl sm:rounded-2xl overflow-hidden [&>button]:hidden"
                  data-testid="keepsake-detail-modal"
                >
                  {(() => {
                    const selected = recipients.find(r => r.id === openBoxId);
                    if (!selected) return null;
                    return (
                      <MemoryBox
                        recipient={selected}
                        savedGifts={savedGiftsByRecipient[selected.id] || []}
                        questionnaires={questionnaires}
                        isOpen={true}
                        onToggle={() => setOpenBoxId(null)}
                        onDelete={() => {
                          setOpenBoxId(null);
                          handleDeleteRecipient(selected.id);
                        }}
                        onAddGift={() => {
                          navigate('/browse', {
                            state: {
                              addGiftMode: true,
                              recipientId: selected.id,
                              recipientName: selected.name,
                              interests: selected.interests || [],
                              preferences: selected.gift_preferences || {}
                            }
                          });
                        }}
                        onRemoveGift={(giftId) => handleRemoveSavedGift(selected.id, giftId)}
                        onCreateQuestionnaire={() => handleCreateQuestionnaire(selected)}
                        onDeleteQuestionnaire={handleDeleteQuestionnaire}
                        onAnalyzeQuestionnaire={handleAnalyzeInline}
                        analyzingId={analyzingId}
                        onManageOccasions={() => handleManageOccasions(selected)}
                        onEditRecipient={() => handleEditRecipient(selected)}
                        onFindGifts={handleFindGifts}
                        onInlineGiftAdded={() => fetchRecipients()}
                        user={user}
                        darkMode={darkMode}
                      />
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Add-recipient button at the bottom of the dashboard */}
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={() => setShowAddRecipient(true)}
                  data-testid="add-recipient-button"
                  className="transition-all hover:scale-105"
                  style={{ background: '#FED7A5', color: '#20212B', borderRadius: '30px', padding: '14px 28px', fontSize: '15px', fontWeight: '600' }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Another Keepsake
                </Button>
              </div>
            </>
          )
        ) : (
          /* Wishlist Tab Content */
          !wishlists || wishlists.length === 0 ? (
            /* Empty State, mirrors the Keepsakes tab empty state (same
                stacked rounded-2xl gradient card + white icon) so the two
                tabs share a single visual language. Different symbol
                (heart vs gift) keeps them distinct. */
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <div className="relative w-48 h-48 mx-auto mb-8">
                  <div className="absolute inset-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #E8B4B8 0%, #D4A4A8 100%)', transform: 'rotate(-5deg)' }} />
                  <div className="absolute inset-4 rounded-2xl" style={{ background: 'linear-gradient(135deg, #B4D4E8 0%, #A4C4D8 100%)', transform: 'rotate(5deg)' }} />
                  <div className="absolute inset-0 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FED7A5 0%, #E8C795 100%)' }}>
                    <Heart className="w-16 h-16" style={{ color: 'white' }} />
                  </div>
                </div>

                <h2 className="text-3xl mb-4 transition-colors" style={{ fontFamily: 'Playfair Display, serif', color: darkMode.textPrimary, fontWeight: '600' }}>
                  Create Your First Wishlist
                </h2>
                <p className="text-base mb-8 leading-relaxed transition-colors" style={{ color: darkMode.textSecondary }}>
                  Organize your favorite gifts into custom wishlists. Choose a name and color for each one.
                </p>
                <Button
                  onClick={() => setShowCreateWishlist(true)}
                  className="transition-all hover:scale-105"
                  style={{ background: '#FED7A5', color: '#20212B', borderRadius: '30px', padding: '16px 32px', fontSize: '16px', fontWeight: '600' }}
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Create Your First Wishlist
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Decorative grid of wishlist cards, mirrors the Keepsakes
                  tab. Click a card to open the full detail view in a modal
                  overlay (replaces the old inline expand-in-place pattern). */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {wishlists.map(wishlist => (
                  <WishlistCard
                    key={wishlist.id}
                    wishlist={wishlist}
                    products={wishlistProducts[wishlist.id] || []}
                    isExpanded={false}
                    onToggle={() => setOpenWishlistId(wishlist.id)}
                    onEdit={() => {
                      setEditingWishlist(wishlist);
                      setShowEditWishlist(true);
                    }}
                    onDelete={() => handleDeleteWishlist(wishlist.id)}
                    onShare={() => setSharingWishlist(wishlist)}
                    onRemoveProduct={(productId) => handleRemoveProductFromWishlist(wishlist.id, productId)}
                    user={user}
                    darkMode={darkMode}
                  />
                ))}
              </div>

              {/* Expanded detail modal, opens for the currently-selected
                  wishlist when its card is clicked. */}
              <Dialog
                open={!!openWishlistId}
                onOpenChange={(open) => { if (!open) setOpenWishlistId(null); }}
              >
                <DialogContent
                  className="max-w-7xl w-[95vw] max-h-[90vh] overflow-y-auto p-0 border-0 bg-transparent shadow-none [&>button]:hidden"
                  data-testid="wishlist-detail-modal"
                >
                  {(() => {
                    const selected = wishlists.find(w => w.id === openWishlistId);
                    if (!selected) return null;
                    return (
                      <WishlistCard
                        wishlist={selected}
                        products={wishlistProducts[selected.id] || []}
                        isExpanded={true}
                        onToggle={() => setOpenWishlistId(null)}
                        onEdit={() => {
                          setEditingWishlist(selected);
                          setShowEditWishlist(true);
                        }}
                        onDelete={() => {
                          setOpenWishlistId(null);
                          handleDeleteWishlist(selected.id);
                        }}
                        onShare={() => setSharingWishlist(selected)}
                        onRemoveProduct={(productId) => handleRemoveProductFromWishlist(selected.id, productId)}
                        user={user}
                        darkMode={darkMode}
                      />
                    );
                  })()}
                </DialogContent>
              </Dialog>

              {/* Add-wishlist button at the bottom of the wishlist grid, matches
                  the "Add Another Keepsake" placement on the Keepsakes tab. */}
              <div className="mt-8 flex justify-center">
                <Button
                  onClick={() => setShowCreateWishlist(true)}
                  data-testid="add-wishlist-button"
                  className="transition-all hover:scale-105"
                  style={{ background: '#FED7A5', color: '#20212B', borderRadius: '30px', padding: '14px 28px', fontSize: '15px', fontWeight: '600' }}
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Another Wishlist
                </Button>
              </div>
            </>
          )
        )}
      </div>

      {/* Modals */}
      <CreateWishlistModal
        isOpen={showCreateWishlist}
        onClose={() => setShowCreateWishlist(false)}
        onSubmit={handleCreateWishlist}
        darkMode={darkMode}
      />

      <EditWishlistModal
        isOpen={showEditWishlist}
        wishlist={editingWishlist}
        onClose={() => {
          setShowEditWishlist(false);
          setEditingWishlist(null);
        }}
        onSubmit={handleUpdateWishlist}
        darkMode={darkMode}
      />

      <AddRecipientDialog
        isOpen={showAddRecipient}
        onClose={setShowAddRecipient}
        newRecipient={newRecipient}
        onRecipientChange={setNewRecipient}
        onSubmit={handleAddRecipient}
        onInterestToggle={handleInterestToggle}
        onAddOccasion={handleAddOccasion}
        onRemoveOccasion={handleRemoveOccasion}
        onOccasionDateChange={handleOccasionDateChange}
        darkMode={darkMode}
      />

      <Footer />
      
      <OccasionsModal
        isOpen={occasionsModal.isOpen}
        onClose={() => setOccasionsModal({ isOpen: false, recipient: null })}
        recipient={occasionsModal.recipient}
        onUpdate={handleUpdateRecipientOccasions}
      />
      
      <EditRecipientModal
        isOpen={editRecipientModal.isOpen}
        recipient={editRecipientModal.recipient}
        formData={editRecipientModal.formData}
        onClose={() => setEditRecipientModal({ isOpen: false, recipient: null, formData: {} })}
        onFormDataChange={(data) => setEditRecipientModal(prev => ({ ...prev, formData: data }))}
        onSave={handleSaveRecipient}
        darkMode={darkMode}
      />
      
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
      />

      {sharingWishlist && (
        <WishlistShareModal
          wishlist={sharingWishlist}
          darkMode={darkMode}
          onClose={() => setSharingWishlist(null)}
          onUpdated={(updated) => {
            setWishlists(prev => prev.map(w => w.id === updated.id ? { ...w, ...updated } : w));
            setSharingWishlist(prev => prev ? { ...prev, ...updated } : prev);
          }}
        />
      )}
    </div>
  );
}
